// Admin Live Preview channel. The admin app hosts preview.html in an
// iframe and posts form changes; the widget applies them in place.

import type { Position } from './types'

export const PREVIEW_ORIGINS = [
  'https://app.airys.ai',
  'http://localhost:5173',
  'http://127.0.0.1:5173',
]

export interface PreviewUpdate {
  primaryColor?: string
  accentColor?: string
  position?: Position
  greeting?: string
  projectName?: string
  logoUrl?: string
}

// Only keeps fields with the right type; a message from an allowed origin
// is still untrusted data.
function parseUpdate(data: unknown): PreviewUpdate | null {
  if (!data || typeof data !== 'object') return null
  const d = data as Record<string, unknown>
  if (d.type !== 'airys:preview:update') return null
  const str = (key: string) => (typeof d[key] === 'string' ? (d[key] as string) : undefined)
  const pos = str('position')
  return {
    primaryColor: str('primaryColor'),
    accentColor: str('accentColor'),
    position: pos === 'bottom_left' || pos === 'bottom_right' ? pos : undefined,
    greeting: str('greeting'),
    projectName: str('projectName'),
    logoUrl: str('logoUrl'),
  }
}

export function installPreviewChannel(
  apply: (update: PreviewUpdate) => void,
  allowedOrigins: string[] = PREVIEW_ORIGINS,
): () => void {
  const onMessage = (e: MessageEvent) => {
    if (!allowedOrigins.includes(e.origin)) return
    const update = parseUpdate(e.data)
    if (update) apply(update)
  }
  window.addEventListener('message', onMessage)
  return () => window.removeEventListener('message', onMessage)
}

// Tells the parent the listener is wired so it can replay the latest
// form values (covers typing before the iframe loaded).
export function postReady() {
  try {
    window.parent.postMessage({ type: 'airys:preview:ready' }, '*')
  } catch {
    // A cross-origin parent may refuse; harmless.
  }
}
